import React from 'react';

import { useGetList, useUpdateList } from '@vlabs/api/artemy/client';
import type { ListId, UpdateListRequest } from '@vlabs/api/artemy/model';
import { EditIcon } from '@vlabs/icons';
import { ListForm } from '@vlabs/monokit/entities/lists';
import { useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface EditListButtonProps extends Omit<IconButtonProps, 'onClick'> {
  listId: ListId;
  onEditList?: VoidFunction;
  enabled?: boolean;
  'data-testid'?: string;
}

const displayName = 'EditListButton';

function EditListButton({
  listId,
  onEditList = undefined,
  enabled = true,
  'data-testid': dataTestId = undefined,
  ...props
}: EditListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const editListModal = useModal();
  const getList = useGetList(listId);
  const updateList = useUpdateList();

  const onSubmit = (data: UpdateListRequest) => {
    updateList.mutate({ listId, data }, {
      onSuccess: () => {
        onEditList?.();
        editListModal.close();
        toast.success(t('lists:подтверждение.обновление.успех', { list: data.user_data }));
      },
    });
  };

  return (
    <React.Fragment>
      {editListModal.wrap(
        <ListForm
          context="update"
          defaultValues={{ user_data: getList.data?.user_data }}
          hint={t('entities:list_form.owner_department_hint')}
          onCancel={editListModal.close}
          onSubmit={onSubmit}
        />,
      )}
      <IconButton
        data-testid={dataTestId ? `${displayName}-${dataTestId}` : 'editCell'}
        disabled={!enabled}
        icon={<EditIcon />}
        onClick={editListModal.open}
        shape="round"
        title={t('lists:widgets.edit list widget.button.edit button.title')}
        {...props}
      />
    </React.Fragment>
  );
}

EditListButton.displayName = displayName;

export { EditListButton };
